import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type SplitTextProps = {
  /** Plain text, or nodes when you need inline markup. */
  text: string;
  className?: string;
  as?: "h1" | "h2" | "h3" | "p" | "span";
  delay?: number;
  /** Stagger between words, in ms. */
  stagger?: number;
  once?: boolean;
};

/**
 * Reveals text word by word with a clipped rise. Each word is its own
 * overflow-hidden box so the type appears to slide up into place.
 */
export function SplitText({
  text,
  className,
  as = "h2",
  delay = 0,
  stagger = 55,
  once = true,
}: SplitTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);
  const words = text.split(" ");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setShown(false);
          }
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -6% 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const Tag = as as "h2";

  return (
    <Tag ref={ref as React.Ref<HTMLHeadingElement>} className={className}>
      {words.map((word, i) => (
        <span
          key={`${word}-${i}`}
          className="inline-block overflow-hidden align-bottom"
          // keeps descenders from being clipped by the overflow box
          style={{ paddingBottom: "0.12em", marginBottom: "-0.12em" }}
        >
          <span
            className={cn(
              "inline-block transition-[transform,opacity] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:translate-y-0",
              shown ? "translate-y-0 opacity-100" : "translate-y-full opacity-0",
            )}
            style={{ transitionDelay: `${delay + i * stagger}ms` }}
          >
            {word}
            {i < words.length - 1 ? "\u00A0" : ""}
          </span>
        </span>
      ))}
    </Tag>
  );
}
